import React, { createContext, useContext, useEffect, useState } from "react";

// Dark/light theme toggle. The actual color values live entirely in
// index.css as CSS custom properties (--bg, --panel, --text, ...) -- every
// component already styles itself via var(--x) rather than hardcoded
// colors, so flipping the `data-theme` attribute on <html> is enough to
// re-theme the whole app with zero per-component changes.
const STORAGE_KEY = "algodesk-theme";
const ThemeContext = createContext(null);

function readStoredTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : "dark";
  } catch {
    // localStorage can throw in private-browsing/locked-down contexts --
    // just fall back to the default rather than crash the app over a
    // theme preference.
    return "dark";
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(readStoredTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore -- theme just won't persist across reloads
    }
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
